import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  propertyAddress: string;
  hasMoreImages?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  totalImageCount?: number;
  isAuthenticated?: boolean;
}

export const ImageLightbox = ({
  images,
  initialIndex,
  isOpen,
  onClose,
  propertyAddress,
  hasMoreImages,
  onLoadMore,
  isLoadingMore,
  totalImageCount,
  isAuthenticated = true,
}: ImageLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchMoveX, setTouchMoveX] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimer, setControlsTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [lastTap, setLastTap] = useState(0);
  const [initialDistance, setInitialDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  // Detect landscape orientation
  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: landscape) and (max-height: 500px)");
    
    const handleOrientationChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsLandscape(e.matches);
      if (e.matches) {
        // In landscape, start with controls visible
        setShowControls(true);
        // Auto-hide after 3 seconds
        const timer = setTimeout(() => setShowControls(false), 3000);
        setControlsTimer(timer);
      } else {
        // In portrait, always show controls
        setShowControls(true);
        if (controlsTimer) clearTimeout(controlsTimer);
      }
    };

    handleOrientationChange(mediaQuery);
    mediaQuery.addEventListener("change", handleOrientationChange);

    return () => {
      mediaQuery.removeEventListener("change", handleOrientationChange);
      if (controlsTimer) clearTimeout(controlsTimer);
    };
  }, []);

  // Reset to initial index when lightbox opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsImageLoading(true);
      setSwipeDirection(null);
      setTouchMoveX(0);
      setShowControls(true);
      setShowSignupPrompt(false);
      
      // Auto-hide controls in landscape after 3 seconds
      if (isLandscape) {
        if (controlsTimer) clearTimeout(controlsTimer);
        const timer = setTimeout(() => setShowControls(false), 3000);
        setControlsTimer(timer);
      }
    }
  }, [isOpen, initialIndex, isLandscape]);

  // Auto-advance when new images are loaded (images.length increases while at the last image)
  const [prevImagesLength, setPrevImagesLength] = useState(images.length);
  useEffect(() => {
    if (images.length > prevImagesLength && currentIndex === prevImagesLength - 1) {
      // New images loaded — advance to the next one
      setIsImageLoading(true);
      setSwipeDirection('left');
      setTimeout(() => {
        setCurrentIndex(prevImagesLength);
        setSwipeDirection(null);
      }, 50);
    }
    setPrevImagesLength(images.length);
  }, [images.length]);

  // Reset zoom when image changes
  useEffect(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
    setIsPanning(false);
  }, [currentIndex]);

  // Preload adjacent images for smoother transitions
  useEffect(() => {
    if (!isOpen) return;
    
    const preloadImage = (src: string) => {
      const img = new Image();
      img.src = src;
    };

    // Preload next image
    if (currentIndex < images.length - 1) {
      preloadImage(images[currentIndex + 1]);
    }
    
    // Preload previous image
    if (currentIndex > 0) {
      preloadImage(images[currentIndex - 1]);
    }
  }, [currentIndex, images, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          handlePrevious();
          break;
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          handleNext();
          break;
        case "Home":
          e.preventDefault();
          setCurrentIndex(0);
          setIsImageLoading(true);
          break;
        case "End":
          e.preventDefault();
          setCurrentIndex(images.length - 1);
          setIsImageLoading(true);
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handlePrevious = () => {
    if (showSignupPrompt) {
      setShowSignupPrompt(false);
      return;
    }
    if (scale > 1) return; // Disable navigation when zoomed
    setSwipeDirection('right');
    setIsImageLoading(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      setSwipeDirection(null);
    }, 50);
  };

  const handleNext = () => {
    if (showSignupPrompt) return;
    if (scale > 1) return;
    if (currentIndex === images.length - 1) {
      if (hasMoreImages && onLoadMore) {
        // At last loaded image with more available — trigger load
        onLoadMore();
        return;
      }
      // Non-authenticated user with more images available — show signup prompt
      if (!isAuthenticated && totalImageCount && totalImageCount > images.length) {
        setShowSignupPrompt(true);
        return;
      }
      // No more images — wrap around
    }
    setSwipeDirection('left');
    setIsImageLoading(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      setSwipeDirection(null);
    }, 50);
  };

  // Calculate distance between two touch points for pinch detection
  const getDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Touch gesture handlers for mobile swipe and pinch zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom start
      const distance = getDistance(e.touches);
      setInitialDistance(distance);
      setInitialScale(scale);
      setTouchStartX(0); // Cancel swipe
    } else if (e.touches.length === 1) {
      const now = Date.now();
      const timeSinceLastTap = now - lastTap;
      
      // Double tap detection
      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        // Double tap to zoom
        if (scale > 1) {
          setScale(1);
          setTranslateX(0);
          setTranslateY(0);
        } else {
          setScale(2.5);
          // Center zoom on tap position
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.touches[0].clientX - rect.left;
          const y = e.touches[0].clientY - rect.top;
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          setTranslateX((centerX - x) * 1.5);
          setTranslateY((centerY - y) * 1.5);
        }
        setLastTap(0);
      } else {
        setLastTap(now);
        // Start pan or swipe
        if (scale > 1) {
          setIsPanning(true);
          setPanStart({ x: e.touches[0].clientX - translateX, y: e.touches[0].clientY - translateY });
        } else {
          setTouchStartX(e.touches[0].clientX);
          setTouchStartTime(Date.now());
          setTouchMoveX(0);
        }
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialDistance > 0) {
      // Pinch zoom
      const currentDistance = getDistance(e.touches);
      const newScale = Math.max(1, Math.min(5, initialScale * (currentDistance / initialDistance)));
      setScale(newScale);
    } else if (e.touches.length === 1) {
      if (scale > 1 && isPanning) {
        // Pan when zoomed
        const newX = e.touches[0].clientX - panStart.x;
        const newY = e.touches[0].clientY - panStart.y;
        setTranslateX(newX);
        setTranslateY(newY);
      } else if (scale === 1 && touchStartX !== 0) {
        // Swipe to navigate
        const currentX = e.touches[0].clientX;
        const diff = currentX - touchStartX;
        setTouchMoveX(diff);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (scale === 1 && touchStartX !== 0) {
      const touchEndX = e.changedTouches[0].clientX;
      const swipeDistance = touchStartX - touchEndX;
      const timeDiff = Date.now() - touchStartTime;
      const velocity = Math.abs(swipeDistance) / timeDiff;

      // Reduced threshold to 30px for more sensitive detection
      // OR fast swipe (velocity > 0.5) for flick gestures
      if (Math.abs(swipeDistance) > 30 || velocity > 0.5) {
        if (swipeDistance > 0) {
          handleNext();
        } else {
          handlePrevious();
        }
      }
    }
    
    setTouchMoveX(0);
    setTouchStartX(0);
    setInitialDistance(0);
    setIsPanning(false);
  };

  // Toggle controls visibility on tap (landscape mode)
  const handleContainerTap = (e: React.MouseEvent | React.TouchEvent) => {
    // Only in landscape mode
    if (!isLandscape) return;
    
    // Don't toggle if user is swiping
    if (touchMoveX !== 0) return;
    
    setShowControls(prev => !prev);
    
    // Reset auto-hide timer
    if (controlsTimer) clearTimeout(controlsTimer);
    const timer = setTimeout(() => setShowControls(false), 3000);
    setControlsTimer(timer);
  };

  if (!isOpen || images.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-[100vw] w-full h-full p-0 bg-black/95 border-none [&>button.opacity-70]:hidden"
        aria-label="Image gallery lightbox"
      >
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-2 right-2 z-50 text-white hover:bg-white/10 rounded-full transition-opacity duration-300 ${
            isLandscape && !showControls ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          onClick={onClose}
          aria-label="Close lightbox"
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Image counter badge */}
        <Badge 
          className={`absolute top-2 left-1/2 -translate-x-1/2 z-50 bg-black/60 text-godyspo-gold border-godyspo-gold/20 text-sm px-3 py-0.5 transition-opacity duration-300 ${
            isLandscape && !showControls ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          aria-live="polite"
        >
          {currentIndex + 1} / {totalImageCount || images.length}
          {isLoadingMore && " (loading...)"}

        </Badge>

        {/* Previous button - hidden on mobile */}
        {images.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10 rounded-full h-12 w-12"
            onClick={handlePrevious}
            aria-label="Previous image"
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
        )}

        {/* Next button - hidden on mobile */}
        {images.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10 rounded-full h-12 w-12"
            onClick={handleNext}
            aria-label="Next image"
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        )}

        {/* Main image container with touch handlers */}
        <div
          className={`flex items-center justify-center w-full h-full md:p-8 ${
            isLandscape ? 'p-0' : 'p-4'
          }`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleContainerTap}
        >
          <div className="relative max-w-full max-h-full flex items-center justify-center">
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-godyspo-gold"></div>
              </div>
            )}
            <img
              src={images[currentIndex]}
              alt={`Property at ${propertyAddress} - Image ${currentIndex + 1} of ${images.length}`}
              className={`max-w-full object-contain ${
                isLandscape ? 'max-h-screen' : 'max-h-[calc(100vh-8rem)]'
              } ${
                swipeDirection === 'left' ? 'animate-slide-in-from-right' :
                swipeDirection === 'right' ? 'animate-slide-in-from-left' : 
                'animate-fade-in'
              } ${scale > 1 ? 'cursor-move' : 'cursor-pointer'}`}
              style={{
                transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px) ${touchMoveX !== 0 && scale === 1 ? `translateX(${touchMoveX}px)` : ''}`,
                opacity: scale === 1 && touchMoveX !== 0 ? Math.max(0.3, 1 - Math.abs(touchMoveX) / 300) : 1,
                transition: isPanning || touchMoveX !== 0 ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onLoad={() => setIsImageLoading(false)}
              draggable={false}
            />
          </div>
        </div>

        {/* Signup prompt overlay for non-authenticated users */}
        {showSignupPrompt && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 animate-fade-in">
            <div className="text-center space-y-6 px-8 max-w-md">
              <div className="mx-auto w-16 h-16 rounded-full bg-godyspo-gold/20 flex items-center justify-center">
                <Camera className="h-8 w-8 text-godyspo-gold" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-white">
                  Want to see all {totalImageCount} photos?
                </h3>
                <p className="text-white/70 text-sm">
                  Sign up for free to view every photo of this property and unlock full access to all listings.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Link
                  to="/buyer/register"
                  className="inline-flex items-center justify-center rounded-md bg-godyspo-gold text-black font-semibold px-6 py-3 hover:bg-godyspo-gold/90 transition-colors"
                >
                  Sign Up Free
                </Link>
                <Button
                  variant="ghost"
                  className="text-white/60 hover:text-white hover:bg-white/10"
                  onClick={() => setShowSignupPrompt(false)}
                >
                  Go Back
                </Button>
              </div>
            </div>
          </div>
        )}

        {images.length > 1 && !isLandscape && (
          <div className="md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Swipe to navigate
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
